import { useEffect, useState } from 'react'
import { api } from '../api/client.ts'
import { Avatar } from './Avatar.tsx'
import { CommentsPanel } from './CommentsPanel.tsx'
import { SendToChatDialog } from './SendToChatDialog.tsx'
import { RemindMe } from './RemindMe.tsx'
import { dateValue, fromInputs, timeValue, KIND_NAMES } from '../lib/planner.ts'

/**
 * СОБЫТИЕ: ЗАВЕСТИ ИЛИ ИЗМЕНИТЬ
 *
 * Повтор выбирается из готовых вариантов, а не набирается правилом:
 * «по вторникам и четвергам» человек в голове держит именно так, а не
 * строкой FREQ=WEEKLY;BYDAY=TU,TH.
 *
 * У повторяющегося события правка всегда спрашивает, что менять —
 * одно занятие или все. Молча изменить всё — худшее, что тут можно
 * сделать: отменили одну пятницу, а исчезли все.
 */

type Person = { id: string; displayName: string; avatarUrl: string | null }

type Calendar = { id: string; name: string; color: string; mine: boolean }

export type EventDraft = {
  id?: string
  occurrenceAt?: string
  repeating?: boolean
  title: string
  description?: string | null
  location?: string | null
  kind: string
  startsAt: string
  endsAt?: string | null
  allDay: boolean
  rrule?: string | null
  calendarId?: string
  participants?: { id: string }[]
}

const REPEATS = [
  { id: '', name: 'Не повторять' },
  { id: 'FREQ=DAILY', name: 'Каждый день' },
  { id: 'FREQ=WEEKLY', name: 'Каждую неделю' },
  { id: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR', name: 'По будням' },
  { id: 'FREQ=WEEKLY;BYDAY=TU,TH', name: 'По вторникам и четвергам' },
  { id: 'FREQ=WEEKLY;BYDAY=MO,WE,FR', name: 'По понедельникам, средам и пятницам' },
  { id: 'FREQ=WEEKLY;INTERVAL=2', name: 'Раз в две недели' },
  { id: 'FREQ=MONTHLY', name: 'Каждый месяц' },
  { id: 'FREQ=YEARLY', name: 'Каждый год' },
]

export function EventSheet({
  draft,
  calendars,
  people,
  onClose,
  onSaved,
}: {
  draft: EventDraft
  calendars: Calendar[]
  people: Person[]
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(draft.id)
  const start = new Date(draft.startsAt)
  const end = draft.endsAt ? new Date(draft.endsAt) : null

  const [title, setTitle] = useState(draft.title)
  const [kind, setKind] = useState(draft.kind)
  const [allDay, setAllDay] = useState(draft.allDay)
  const [date, setDate] = useState(dateValue(start))
  const [time, setTime] = useState(timeValue(start))
  const [endTime, setEndTime] = useState(end ? timeValue(end) : '')
  const [rrule, setRrule] = useState(draft.rrule ?? '')
  const [calendarId, setCalendarId] = useState(
    draft.calendarId ?? calendars.find((c) => c.mine)?.id ?? calendars[0]?.id ?? '',
  )
  const [location, setLocation] = useState(draft.location ?? '')
  const [description, setDescription] = useState(draft.description ?? '')
  const [invited, setInvited] = useState<string[]>(draft.participants?.map((p) => p.id) ?? [])

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [scope, setScope] = useState<'one' | 'all' | null>(null)

  // Повтор бывает не из списка — например, у события, заведённого раньше
  // или пришедшего из другого календаря. Не теряем его молча
  const [custom] = useState(() => (draft.rrule && !REPEATS.some((r) => r.id === draft.rrule) ? draft.rrule : null))

  const save = async (onlyThis = false) => {
    setBusy(true)
    setError(null)

    try {
      const startsAt = fromInputs(date, allDay ? null : time)
      const endsAt = !allDay && endTime ? fromInputs(date, endTime) : null

      if (endsAt && endsAt < startsAt) {
        throw new Error('Событие не может кончиться раньше, чем началось')
      }

      if (onlyThis && draft.id) {
        await api(`/api/planner/events/${draft.id}`, {
          method: 'PATCH',
          body: {
            onlyThis: true,
            occurrenceAt: draft.occurrenceAt,
            title: title.trim(),
            startsAt: startsAt.toISOString(),
            endsAt: endsAt?.toISOString() ?? null,
          },
        })
      } else {
        const body = {
          calendarId,
          title: title.trim(),
          kind,
          allDay,
          startsAt: startsAt.toISOString(),
          endsAt: endsAt?.toISOString() ?? null,
          rrule: rrule || null,
          location: location.trim() || null,
          description: description.trim() || null,
          participantIds: invited,
        }

        if (draft.id) {
          await api(`/api/planner/events/${draft.id}`, { method: 'PATCH', body })
        } else {
          await api('/api/planner/events', { method: 'POST', body })
        }
      }

      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не сохранилось')
    } finally {
      setBusy(false)
      setScope(null)
    }
  }

  const remove = async (onlyThis: boolean) => {
    if (!draft.id) return
    setBusy(true)

    try {
      if (onlyThis) {
        await api(`/api/planner/events/${draft.id}`, {
          method: 'PATCH',
          body: { onlyThis: true, occurrenceAt: draft.occurrenceAt, cancelled: true },
        })
      } else {
        await api(`/api/planner/events/${draft.id}`, { method: 'DELETE' })
      }
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалилось')
    } finally {
      setBusy(false)
      setScope(null)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{editing ? 'Событие' : 'Новое событие'}</h2>
          {editing && (
            <button
              className="icon-btn"
              onClick={() => setSending(true)}
              title="Отправить в переписку"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M3 20.5 21 12 3 3.5 3 10l12 2-12 2z" />
              </svg>
            </button>
          )}
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <label className="field">
          <span className="field-label">Что</span>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Плавание, приём у врача, день рождения"
            maxLength={200}
            autoFocus={!editing}
          />
        </label>

        <label className="field">
          <span className="field-label">Какое это событие</span>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(KIND_NAMES)
              .filter(([id]) => id !== 'BIRTHDAY')
              .map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
          </select>
        </label>

        <div className="amount-row">
          <label className="field">
            <span className="field-label">Когда</span>
            <input
              className="input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>

          {!allDay && (
            <>
              <label className="field">
                <span className="field-label">Начало</span>
                <input
                  className="input"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Конец</span>
                <input
                  className="input"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </label>
            </>
          )}
        </div>

        <label className="check-row">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
          <span>Весь день</span>
        </label>

        <label className="field">
          <span className="field-label">Повторять</span>
          <select className="input" value={rrule} onChange={(e) => setRrule(e.target.value)}>
            {REPEATS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
            {custom && <option value={custom}>Свой порядок повторов</option>}
          </select>
        </label>

        {calendars.filter((c) => c.mine).length > 1 && (
          <label className="field">
            <span className="field-label">В какой календарь</span>
            <select
              className="input"
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
            >
              {calendars
                .filter((c) => c.mine)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
        )}

        <label className="field">
          <span className="field-label">Где</span>
          <input
            className="input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Не обязательно"
            maxLength={200}
          />
        </label>

        {people.length > 0 && (
          <>
            <h3 className="sheet-sub">Кого зовём</h3>
            <div className="people-row">
              {people.map((person) => (
                <button
                  key={person.id}
                  className={'person-chip' + (invited.includes(person.id) ? ' is-picked' : '')}
                  onClick={() =>
                    setInvited((current) =>
                      current.includes(person.id)
                        ? current.filter((id) => id !== person.id)
                        : [...current, person.id],
                    )
                  }
                >
                  <Avatar
                    id={person.id}
                    name={person.displayName}
                    url={person.avatarUrl}
                    size={24}
                  />
                  <span>{person.displayName}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <label className="field">
          <span className="field-label">Подробности</span>
          <textarea
            className="input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Не обязательно"
            maxLength={2000}
          />
        </label>

        {editing && draft.id && (
          <>
            <h3 className="sheet-sub">Напомнить мне</h3>
            <p className="sheet-note">
              За полчаса до события уведомление придёт само. Это — если нужно
              вспомнить раньше.
            </p>
            <RemindMe entityType="EVENT" entityId={draft.id} title={draft.title} />

            <h3 className="sheet-sub">Обсуждение</h3>
            <CommentsPanel entityType="EVENT" entityId={draft.id} />
          </>
        )}

        {error && <div className="sheet-error">{error}</div>}

        <div className="sheet-actions">
          <button
            className="btn-primary"
            disabled={busy || !title.trim()}
            onClick={() => {
              // У повторяющегося события всегда спрашиваем, что менять
              if (editing && draft.repeating) setScope('one')
              else void save()
            }}
          >
            {busy ? '…' : 'Сохранить'}
          </button>

          {editing && (
            <button
              className="btn-quiet"
              disabled={busy}
              onClick={() => {
                if (draft.repeating) setScope('all')
                else if (window.confirm('Удалить событие?')) void remove(false)
              }}
            >
              Удалить
            </button>
          )}
        </div>

        {scope && (
          <div className="scope-ask">
            <p className="sheet-text">
              {scope === 'one'
                ? 'Это повторяющееся событие. Изменить только это занятие или все?'
                : 'Это повторяющееся событие. Отменить только это занятие или все?'}
            </p>
            <div className="sheet-actions">
              <button
                className="btn-primary"
                disabled={busy}
                onClick={() => (scope === 'one' ? void save(true) : void remove(true))}
              >
                Только это
              </button>
              <button
                className="btn-quiet"
                disabled={busy}
                onClick={() => (scope === 'one' ? void save(false) : void remove(false))}
              >
                Все
              </button>
              <button className="btn-quiet" disabled={busy} onClick={() => setScope(null)}>
                Отмена
              </button>
            </div>
          </div>
        )}

        {sending && draft.id && (
          <SendToChatDialog
            entityType="EVENT"
            entityId={draft.id}
            title={draft.title}
            onClose={() => setSending(false)}
          />
        )}
      </div>
    </div>
  )
}

/** Кого можно позвать: домочадцы, кроме себя. */
export function useFamilyPeople(meId: string | undefined): Person[] {
  const [people, setPeople] = useState<Person[]>([])

  useEffect(() => {
    api<{ family: { members: (Person & { role: string })[] } }>('/api/family')
      .then((data) => setPeople(data.family.members.filter((m) => m.id !== meId)))
      .catch(() => setPeople([]))
  }, [meId])

  return people
}
